<template>
    <!--
        The v4 wrapper renders its container `height: 100vh` in normal flow,
        where v3's was `position: absolute` and took no part in it. Anything
        above it therefore pushes a full viewport of editor past the fold. So
        the page owns the viewport here - exactly one screen tall, clipped -
        and the editor fills whatever the banner leaves, rather than the two
        of them adding up to more than the window.
    -->
    <div class="flex h-screen w-full flex-col overflow-hidden">
        <SessionExpiredBanner
            class="z-[9999999] shrink-0"
            @sign-in="openSignIn"
            @retry="retrySave"
        ></SessionExpiredBanner>
        <div class="relative min-h-0 flex-1">
            <div
                v-if="loading"
                class="absolute inset-0 z-[999998] flex items-start justify-center pt-96"
            >
                <LoadingSpinner size="large" :delay="false"></LoadingSpinner>
            </div>
            <!--
                `invisible` rather than `v-show`: editor v4 sizes its iframe
                from the container at render time, and `display: none` hands it
                a zero-height box it never recovers from. The style overrides
                the wrapper's own inline 100vh - attribute fallthrough merges
                last, so it wins - and fills this box instead.
            -->
            <EmailEditor
                ref="editorRef"
                :class="{ invisible: loading }"
                :style="{ height: '100%' }"
                :options="topolOptions"
                :callbacks="callbacks"
                :stage="props.stage"
            ></EmailEditor>
        </div>
    </div>
</template>

<script lang="ts" setup>
/**
 * The Email Editor v4 integration. Reached only by teams with the `editor_v4`
 * feature switched on; everyone else gets EditorV3.vue. Pages/Editor/index.vue
 * and Pages/Kiosk/Editor/index.vue pick between them.
 */
import {
    EmailEditor,
    IEmailCallbacks,
    IEmailEditorInstance,
    IStage,
    IVueEmailOptions,
} from '@topol.io/editor-vue';

import LoadingSpinner from '@components/Atomic/LoadingSpinner/LoadingSpinner.vue';
import SessionExpiredBanner from './components/SessionExpiredBanner.vue';

import { ref } from 'vue';
import { router } from '@inertiajs/vue3';
import axios from 'axios';

import useSavedBlocksService, {
    ISavedBlockData,
} from './composables/useSavedBlocksService';
import useSendTestService from './composables/useSendTestService';
import { create as createNotification } from '@composables/useNotifications';
import useEditorSession, {
    EDITOR_REQUEST_HEADERS,
    markSaved,
} from '@composables/useEditorSession';

import useConfirm from '@composables/useConfirm';
import useFonts from '@/Pages/EditorPreferences/Fonts/composables/useFonts';
import useEditorTranslations from './composables/useEditorTranslations';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import ICurrentTeam from '@/types/Members/ICurrentTeam';
import usePusher from '@/composables/usePusher';
import useCookiebot from '@/composables/useCookiebot';
import { useGtm } from '@/composables/useGtm';
import { useSubscriptionLabel } from '@/composables/useSubscriptionLabel';

const { startEcho, stopEcho } = usePusher();
const { show } = useConfirm();

type CurrentUser = {
    id: number;
    name: string;
    profile_photo_path: string;
    profile_photo_url: string;
};

interface Props {
    apiKey: string;
    token: string;
    definition: string;
    saveUrl: string;
    pingUrl: string;
    backUrl: string;
    savedBlocks?: ISavedBlockData[];
    saveSavedBlockUrl: string;
    /**
     * Set only where the editor talks to the SAVED_SECTIONS API directly, which
     * is what unlocks synced sections. Left unset (Kiosk) the editor keeps the
     * callback based saved blocks below.
     */
    savedSectionsUrl?: string;
    teamId?: number;
    /**
     * Set where the template is still exactly what "create from scratch"
     * produced, and nothing has been done to it yet.
     */
    startFromScratch?: boolean;
    /**
     * A template being written has a subject; a premade one in the library is
     * not addressed to anybody, so Kiosk leaves this unset.
     */
    enableSubjectLine?: boolean;
    premadeTemplatesUrl?: string;
    premadeTemplateCategoriesUrl?: string;
    premadeTemplateKeywordsUrl?: string;
    getConversationsUrl: string;
    getCommentsUrl: string;
    sendTestEmailUrl: string;
    /**
     * Editor v4 derives listing, reading and writing an autosave from this one
     * base url, the way it already did for saved sections. The v3 pair of
     * AUTOSAVE / GET_AUTOSAVE endpoints is gone.
     */
    getAutosavesUrl: string;
    theme: string;
    fontSizes: string;
    defaultFonts: string;
    customFonts: string;
    smartMergeTags: string;
    mergeTags: string;
    currentUser: CurrentUser;
    ownerId: string | number;
    templateId: number;
    language?: string;
    colors: string;
    title?: string;
    stage?: IStage;
    enableMergeTagPreview?: boolean;
    imageCompressionOptions: string;
    enableImageToTextRatio: boolean;
    chatAI: boolean;
    googlePromotionalAnnotation: boolean;
    lambdaStage: string;
    userRole: string;
    getFeedsUrl: string;
    getProductsUrl: string;
    templateStatus: string | null;
    enableComments?: boolean;
    renameTemplate?: boolean;
    htmlMinified: boolean;
    enableSectionVariants: boolean;
    emailServiceProvider?: 'mailchimp'|'sparkpost';
    isSubscribed: boolean;
    testingEmails: string;
    teamUsers: string;
    multilingual: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    savedBlocks: () => [],
    savedSectionsUrl: undefined,
    teamId: undefined,
    startFromScratch: false,
    enableSubjectLine: false,
    premadeTemplatesUrl: undefined,
    premadeTemplateCategoriesUrl: undefined,
    premadeTemplateKeywordsUrl: undefined,
    language: 'en',
    title: '',
    stage: 'production',
    enableComments: true,
    renameTemplate: true,
});

const globalProps = computed(() => usePage<ITopolPageProps>().props);
const teamProps = computed(() => globalProps.value.team as ICurrentTeam);

/**
 * The editor is one instance per mounted component in v4, reached through the
 * wrapper rather than a `window` singleton. It only exists between render and
 * unmount, so every call site has to tolerate its absence.
 */
const editorRef = ref<InstanceType<typeof EmailEditor>>();
const editor = (): IEmailEditorInstance | null =>
    editorRef.value?.getEditor() ?? null;

/**
 * Every v4 method crosses the iframe boundary and returns a promise, where the
 * v3 singleton returned nothing. The calls below are all fire and forget -
 * a presence update, a pusher message, a rename - and are reached from places
 * that can run before the editor is ready or after it has gone. None is worth
 * failing the page over, but a floating rejection is worth not leaving behind.
 */
const callEditor = (
    call: (instance: IEmailEditorInstance) => Promise<void>
): void => {
    const instance = editor();

    if (instance === null) {
        return;
    }

    call(instance).catch((error) => {
        console.error('Topol editor call failed', error);
    });
};

/**
 * Editor v4 dropped the `develop` lambda stage. The value is a team feature
 * flag, so an unrecognised one would otherwise fail the editor's own options
 * validation and leave a team with a blank screen.
 */
const LAMBDA_STAGES = [
    'local',
    'production',
    'stage1',
    'stage2',
    'stage3',
    'v4',
] as const;

type LambdaStage = (typeof LAMBDA_STAGES)[number];

const lambdaStage = computed<LambdaStage>(() =>
    (LAMBDA_STAGES as readonly string[]).includes(props.lambdaStage)
        ? (props.lambdaStage as LambdaStage)
        : 'production'
);

const syncedSectionsEnabled = computed(
    () => props.savedSectionsUrl !== undefined
);
const { hideCookieBot, showCookieBot } = useCookiebot();
const { pushEvent } = useGtm();
const { planName } = useSubscriptionLabel();

onMounted(() => {
    hideCookieBot();

    listenForComments();
    listenForSyncedSections();
    listenForActiveMembers();

    heartbeatTimer = setInterval(() => ping(), HEARTBEAT_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
});

onUnmounted(() => {
    showCookieBot();

    stopListeningForActiveMembers();
    stopListeningForComments();

    if (heartbeatTimer !== undefined) {
        clearInterval(heartbeatTimer);
    }
    document.removeEventListener('visibilitychange', onVisibilityChange);
});

type PresenceMember = {
    id: number;
    name: string;
};

const activeMembers = ref<PresenceMember[]>([]);

/**
 * Only worth showing where somebody else could actually turn up. Read from the
 * template's own team rather than the current one, so it stays right for a user
 * who is switched elsewhere.
 */
const hasTeammates = computed(() => {
    try {
        return (JSON.parse(props.teamUsers) as unknown[]).length > 0;
    } catch {
        return false;
    }
});

const publishActiveMembers = () => {
    callEditor((instance) =>
        instance.setActiveMembers(
            activeMembers.value
                .filter((member) => member.id !== props.currentUser.id)
                .map((member) => member.name)
        )
    );
};

/**
 * Presence, not co-editing: the editor has no idea who else is in a template
 * unless we tell it, and telling it does not stop two people saving over each
 * other. It only makes the collision visible.
 */
const listenForActiveMembers = () => {
    if (!hasTeammates.value) {
        return;
    }

    startEcho();

    window.Echo.join('template.' + props.templateId)
        .here((members: PresenceMember[]) => {
            activeMembers.value = members;
            publishActiveMembers();
        })
        .joining((member: PresenceMember) => {
            // Pusher keys presence members by id, so a second tab is the same
            // member rather than a second one - but guard anyway.
            if (!activeMembers.value.some((known) => known.id === member.id)) {
                activeMembers.value.push(member);
            }

            publishActiveMembers();
        })
        .leaving((member: PresenceMember) => {
            activeMembers.value = activeMembers.value.filter(
                (known) => known.id !== member.id
            );

            publishActiveMembers();
        });
};

const stopListeningForActiveMembers = () => {
    if (hasTeammates.value) {
        window.Echo?.leave('template.' + props.templateId);
    }
};

const listenForComments = () => {
    if (teamProps.value.personal_team === false) {
        startEcho();

        window.Echo.private(
            'template-conversations.' + props.templateId
        ).listen(
            '.App\\Topol\\Templates\\Events\\TemplateConversationUpdated',
            (e) => {
                if (e.comment_by !== props.currentUser.id) {
                    callEditor((instance) => instance.refreshComments(e.key));
                }
            }
        );
    }
};

/**
 * A teammate editing a synced section changes content sitting inside the
 * template that is open right now, so pull the new content in place instead of
 * leaving a stale copy on screen until the next reload.
 *
 * Unlike comments this is not gated on being in a shared team - a personal team
 * has synced sections too, and the same user can have the editor open twice.
 */
const listenForSyncedSections = () => {
    if (!syncedSectionsEnabled.value || props.teamId === undefined) {
        return;
    }

    startEcho();

    // Every subscribed editor refreshes, the author's included. Skipping the
    // author would have to key on the connection that made the change, and the
    // edit is sent by the editor iframe on its own origin, which carries no
    // socket id to skip by - so the only available filter is the user, and that
    // silently strands a second window belonging to the same person. Pulling
    // content the author just saved costs them nothing; leaving another window
    // stale is the thing this feature exists to prevent.
    window.Echo.private('team-synced-sections.' + props.teamId).listen(
        '.synced-section-changed',
        () => {
            callEditor((instance) => instance.refreshSyncedSections());
        }
    );
};

const stopListeningForComments = () => {
    stopEcho();
};

const { defaultFonts: allDefaultFonts } = useFonts();
const loading = ref(true);

const {
    createSavedBlock,
    deleteSavedBlock,
    editSavedBlock,
    prepareSavedBlocks,
} = useSavedBlocksService(
    props.savedBlocks,
    props.saveSavedBlockUrl,
    props.language ?? 'en'
);

/**
 * The subject the editor has on screen right now, which is not necessarily what
 * is saved - someone can type one and send a test before saving. The plugin
 * hands us the live definition on test send, so read it from there and let the
 * backend fall back to the saved copy when there is nothing to read.
 *
 * Multilingual templates keep one subject per language mutation; the primary is
 * the one a test send is about.
 */
const subjectFromDefinition = (json: unknown): string | undefined => {
    const definition = json as {
        attributes?: { 'mj-subject'?: { text?: string } };
        langs?: {
            primary?: boolean;
            translations?: { 'mj-subject'?: { content?: string } };
        }[];
    } | null;

    const mutation =
        definition?.langs?.find((lang) => lang.primary) ?? definition?.langs?.[0];

    const subject =
        mutation?.translations?.['mj-subject']?.content ??
        definition?.attributes?.['mj-subject']?.text;

    return subject?.trim() || undefined;
};

const { sendTestEmail } = useSendTestService(props.sendTestEmailUrl);

const { translations: editorTranslations, getTranslations } =
    useEditorTranslations(props.language ?? 'en');

// @ts-ignore
const translations = ref(editorTranslations[props.language ?? 'en']);

const { ping, isRetrying } = useEditorSession(props.pingUrl);

const HEARTBEAT_INTERVAL_MS = 60_000;
let heartbeatTimer: ReturnType<typeof setInterval> | undefined;

const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
        ping();
    }
};

const saveDefinition = (json: unknown, html: unknown) =>
    axios.post(
        props.saveUrl,
        {
            definition: JSON.stringify(json),
            html: html,
        },
        { headers: EDITOR_REQUEST_HEADERS }
    );

const openSignIn = (): void => {
    window.open(route('login'), '_blank');
};

const retrySave = async (): Promise<void> => {
    if (isRetrying.value) {
        return;
    }

    isRetrying.value = true;

    const stillValid = await ping();

    if (!stillValid) {
        createNotification({
            title: 'Still signed out — sign in first.',
            type: 'error',
        });

        isRetrying.value = false;

        return;
    }

    // Re-fire onSave with the CURRENT editor content rather than replaying a
    // stale snapshot captured at the original failed save. isRetrying stays set
    // until onSave settles the re-fired save (see its `finally`), so a second
    // click can't trigger a duplicate concurrent save.
    //
    // v4 resolves save() only once our own onSave has settled, so the catch is
    // for a save that never reaches the handler at all - without it the guard
    // would stay set and the retry button stay dead.
    try {
        await editor()?.save();
    } catch (e) {
        isRetrying.value = false;
    }
};

const getEditorRole = () => {
    switch (props.userRole) {
        case 'owner':
        case 'admin':
            return 'manager';
        case 'editor':
            return 'editor';
        case 'viewer':
            return 'reader';
        default:
            return 'reader';
    }
};

const callbacks: IEmailCallbacks = {
    async onSave({ json, html }) {
        try {
            await saveDefinition(json, html);

            markSaved();

            createNotification({
                title: translations.value.saveSuccess,
                type: 'success',
            });
        } catch (e) {
            // A 401/419 has already raised the session banner via the axios
            // interceptor; any other error is toasted there too. Either way we
            // must not show a false "Saved!" message.
        } finally {
            // Release the retry guard once the (possibly re-fired) save settles.
            isRetrying.value = false;
        }
    },

    async updateTestingEmailAddresses({ emails }) {
        await axios.put(
            route('editor-preferences.testing-emails.update'),
            {
                emails: emails,
            },
            { headers: EDITOR_REQUEST_HEADERS }
        );
    },

    async onSaveAndClose({ json, html }) {
        try {
            await saveDefinition(json, html);

            markSaved();

            router.visit(props.backUrl);
        } catch (e) {
            // Session expired or the save failed - stay in the editor so the
            // banner can warn the user instead of navigating away from unsaved
            // work.
        }
    },

    async onSavedSectionSave({ section }) {
        const newSavedBlocks = await createSavedBlock(
            section as ISavedBlockData
        );

        if (newSavedBlocks !== undefined) {
            callEditor((instance) => instance.setSavedSections(newSavedBlocks));

            createNotification({
                title: translations.value.saveBlockSuccess,
                text: ``,
                type: 'success',
            });
        }
    },

    async onSavedSectionRemove({ id }) {
        const newSavedBlocks = await deleteSavedBlock(Number(id));

        if (newSavedBlocks !== undefined) {
            callEditor((instance) => instance.setSavedSections(newSavedBlocks));

            createNotification({
                title: translations.value.removeBlockSuccess,
                text: ``,
                type: 'success',
            });
        }
    },

    async onSavedSectionEdit({ id }) {
        const newSavedBlocks = await editSavedBlock(Number(id));

        if (newSavedBlocks !== undefined) {
            callEditor((instance) => instance.setSavedSections(newSavedBlocks));

            createNotification({
                title: translations.value.updateBlockSuccess,
                text: ``,
                type: 'success',
            });
        }
    },

    async onTestSend({ email, json, html }) {
        const recipients = Array.isArray(email) ? email : [email];

        if (!props.isSubscribed) {
            createNotification({
                title: 'Email not sent',
                text: `You need to be a paid user to send test emails. Please select an appropriate ${planName.value} subscription to continue.`,
                type: 'error',
            });

            return;
        }

        try {
            const res = await sendTestEmail(
                recipients,
                html,
                subjectFromDefinition(json)
            );

            if (res.data.success === true) {
                createNotification({
                    title: translations.value.sentEmailSuccessTitle,
                    text: getTranslations('sentEmailSuccess', recipients),
                    type: 'success',
                });
                pushEvent('topolpro_send_test_link');
            } else {
                createNotification({
                    title: translations.value.sentEmailFailedTitle,
                    text: getTranslations('sentEmailFailed', recipients),
                    type: 'error',
                });
            }
        } catch (err) {
            createNotification({
                title: translations.value.sentEmailFailedTitle,
                text: getTranslations('sentEmailFailed', recipients),
                type: 'error',
            });
        }
    },

    onAlert({ notification }) {
        if (!notification.expectSideEffect) {
            createNotification({
                title: notification.title,
                text: notification.text,
                type: notification.type,
                persistent: notification.persistent,
            });
        }
    },

    onInit() {
        // Loading anything replaces the default the editor just built for
        // itself, and that default is precisely what it compares against to
        // decide whether to offer the premade picker. A scratch template has
        // nothing worth keeping - it is a stored blank - so leaving it unloaded
        // costs nothing and lets the editor make the offer.
        if (props.startFromScratch) {
            // onLoaded is what normally reveals the editor, and it only fires in
            // answer to a load. Nothing is being loaded here, so it never will -
            // reveal it now instead. The plugin is ready by the time onInit runs.
            loading.value = false;

            return;
        }

        // onLoaded is what clears the spinner, and it only fires for a load
        // that succeeded. v4 rejects a failed one instead of going quiet, so
        // take the chance to stop showing a spinner that will never resolve -
        // onEditorError carries the message that explains it.
        editor()
            ?.load(props.definition)
            .catch(() => {
                loading.value = false;
            });
    },

    onLoaded() {
        loading.value = false;
    },

    /**
     * New in v4, and nothing was listening for the v3 equivalent - a failed
     * authorization used to leave the spinner turning with nothing said. The
     * editor exports no methods after an authentication failure, so there is
     * nothing to retry with: the message is the whole of what we can offer.
     */
    onEditorError({ source, message }) {
        loading.value = false;

        console.error('Topol editor error', source, message);

        createNotification({
            title: 'Editor error',
            text: message,
            type: 'error',
        });
    },

    async onEditorClose() {
        const confirmed = await show({
            type: 'danger',
            header: translations.value.closeEditorTitle,
            message: translations.value.closeEditorText,
            confirm: translations.value.closeEditorConfirm,
            cancel: translations.value.cancel,
        });

        if (confirmed) {
            router.visit(props.backUrl);
        }
    },
    async onTemplateRename() {
        const newTemplateName = await show({
            header: translations.value.templateRenameTitle,
            hasInput: true,
            inputData: {
                label: translations.value.templateRenameLabel,
                defaultValue: props.title,
            },
            confirm: translations.value.rename,
            cancel: translations.value.cancel,
        });

        if (typeof newTemplateName === 'string') {
            try {
                const result = await axios.put(
                    route('templates.update', {
                        template: props.templateId,
                    }),
                    {
                        name: newTemplateName,
                        status: props.templateStatus,
                    },
                    { headers: EDITOR_REQUEST_HEADERS }
                );
                if (result.data.success) {
                    createNotification({
                        title: translations.value.templateRenameSuccessTitle,
                        type: 'success',
                    });

                    callEditor((instance) =>
                        instance.setTemplateName(newTemplateName)
                    );
                    router.reload({ only: ['title'] });
                } else {
                    createNotification({
                        title: translations.value.templateRenameFailedTitle,
                        type: 'error',
                    });
                }
            } catch (e) {
                return;
            }
        }
    },
};

const topolOptions: IVueEmailOptions = {
    apiAuthorizationHeader: 'Bearer ' + props.token,

    title: props.title,

    authorize: {
        apiKey: props.apiKey,
        userId: props.ownerId,
    },

    currentUser: {
        /**
         * Sent as a number, matching the integers our comments API puts in a
         * comment's `read_by`. The editor decides whether a comment is unread
         * with `read_by.indexOf(currentUser.userId)`, which is strict equality,
         * so a string here makes every comment - including your own, the moment
         * you write it - read as unread forever.
         *
         * The editor's own types say this is a string, but nothing validates
         * options at runtime, and `read_by` is what it is actually compared
         * against. See TPL-2697.
         */
        userId: props.currentUser.id,
        name: props.currentUser.name,
        profilePhotoUrl:
            props.currentUser.profile_photo_path ??
            props.currentUser.profile_photo_url,
    },

    role: getEditorRole(),

    enableAutosaves: true,

    // `true` hands the whole saved section panel over to the SAVED_SECTIONS
    // API, which is where synced sections live. An array keeps the old callback
    // mode.
    savedSections: syncedSectionsEnabled.value
        ? true
        : prepareSavedBlocks(props.savedBlocks),
    syncedSectionsEnabled: syncedSectionsEnabled.value,

    // The subject rides inside the template JSON, so it arrives through the
    // existing onSave with no plumbing of its own - per language mutation too,
    // since the editor already runs multilingual.
    //
    // Asked for by the surface rather than inferred from something else that
    // happens to be set: whether a template has a subject is a fact about the
    // editor you are in, not about which urls it was handed.
    enableSubjectLine: props.enableSubjectLine,

    // Offers "start from a predefined template" instead of a blank canvas, and
    // only where there is somewhere to read them from: the Kiosk editor shares
    // this component and is given no url, so switching it on unconditionally
    // handed that editor a picker that could only apologise.
    //
    // premadeTemplatesOptions stays at its defaults for a related reason -
    // showDelete surfaces a Delete button wired to a Remove Template endpoint
    // that does not exist here.
    premadeTemplates: props.premadeTemplatesUrl !== undefined,

    api: {
        AUTOSAVES: props.getAutosavesUrl,
        CONVERSATIONS: props.getConversationsUrl,
        COMMENTS: props.getCommentsUrl,
        FEEDS: props.getFeedsUrl,
        PRODUCTS: props.getProductsUrl,
        ...(props.savedSectionsUrl
            ? { SAVED_SECTIONS: props.savedSectionsUrl }
            : {}),
        // Without these the picker has nowhere to read from and says so, in a
        // toast, instead of offering anything.
        ...(props.premadeTemplatesUrl
            ? { PREMADE_TEMPLATES: props.premadeTemplatesUrl }
            : {}),
        ...(props.premadeTemplateCategoriesUrl
            ? {
                  PREMADE_TEMPLATE_CATEGORIES:
                      props.premadeTemplateCategoriesUrl,
              }
            : {}),
        ...(props.premadeTemplateKeywordsUrl
            ? {
                  PREMADE_TEMPLATES_KEYWORDS:
                      props.premadeTemplateKeywordsUrl,
              }
            : {}),
    },

    windowBar: ['close', 'fullscreen'],

    smartMergeTags: JSON.parse(props.smartMergeTags),
    mergeTags: JSON.parse(props.mergeTags),

    mobileFirstEnabled: true,

    //we handle alerts on our own, to be able reflect our server request responses statuses
    disableAlerts: true,

    language: props.language as IVueEmailOptions['language'],
    fontSizes: JSON.parse(props.fontSizes),

    theme: {
        preset: props.theme === 'light' ? 'light' : 'dark',
        colors: {
            active: '#FFC000',
        },
    },
    colors: JSON.parse(props.colors),

    betaFeatures: {
        carousel: true,
    },

    customFonts:
        props.customFonts === '[]' && props.defaultFonts === '[]'
            ? {
                  override: false,
                  fonts: [],
              }
            : {
                  override: true,
                  fonts:
                      props.defaultFonts === '[]'
                          ? allDefaultFonts.concat(
                                JSON.parse(props.customFonts)
                            )
                          : JSON.parse(props.defaultFonts).concat(
                                JSON.parse(props.customFonts)
                            ),
              },

    enableMergeTagPreview: props.enableMergeTagPreview ?? false,
    imageCompressionOptions: JSON.parse(props.imageCompressionOptions),
    enableImageToTextRatio: props.enableImageToTextRatio,
    chatAI: props.chatAI,
    googlePromotionalAnnotation: props.googlePromotionalAnnotation,
    lambdaStage: lambdaStage.value,
    enableComments: props.enableComments,
    renameTemplate: props.renameTemplate,
    htmlMinified: props.htmlMinified,
    enableSectionVariants: props.enableSectionVariants,
    emailServiceProvider: props.emailServiceProvider,
    imageEditorV2: true,
    testingEmails: props.testingEmails ? JSON.parse(props.testingEmails) : [],
    teamUsers: JSON.parse(props.teamUsers),
    multilingual: props.multilingual,
    enableDarkMode: true,
};
</script>
